import { useState, createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  ArrowRight, 
  BookOpen, 
  Cpu, 
  FileText, 
  Building2, 
  Tag, 
  Hash, 
  Zap,
  Lightbulb,
  AlertTriangle,
  Wrench
} from "lucide-react";
import type { GlossaryEntry, Campaign, CampaignPlacement, CampaignAsset } from "@shared/schema";

function getAssetImageUrl(asset: CampaignAsset): string {
  if (!asset.imageUrl) return "";
  if (asset.imageUrl.startsWith("/objects/") || asset.imageUrl.includes("/objects/uploads/")) {
    return `/api/campaign-assets/${asset.id}/image`;
  }
  return asset.imageUrl;
}

interface CampaignWithPlacements extends Campaign {
  placements: (CampaignPlacement & { asset: CampaignAsset })[];
}

const KEYWORD_TYPE_CONFIG: Record<string, { label: string; icon: typeof Cpu; color: string; headerBg: string }> = {
  technical_term: { 
    label: "Technical Term", 
    icon: Cpu, 
    color: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    headerBg: "bg-gradient-to-r from-blue-500/10 to-blue-600/5"
  },
  standard: { 
    label: "Standard", 
    icon: FileText, 
    color: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
    headerBg: "bg-gradient-to-r from-purple-500/10 to-purple-600/5"
  },
  product_brand: { 
    label: "Brand", 
    icon: Building2, 
    color: "bg-green-500/10 text-green-600 dark:text-green-400",
    headerBg: "bg-gradient-to-r from-green-500/10 to-green-600/5"
  },
  product_description: { 
    label: "Product", 
    icon: Tag, 
    color: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    headerBg: "bg-gradient-to-r from-orange-500/10 to-orange-600/5"
  },
  acronym: { 
    label: "Acronym", 
    icon: Hash, 
    color: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400",
    headerBg: "bg-gradient-to-r from-indigo-500/10 to-indigo-600/5"
  },
  concept: { 
    label: "Concept", 
    icon: Zap, 
    color: "bg-pink-500/10 text-pink-600 dark:text-pink-400",
    headerBg: "bg-gradient-to-r from-pink-500/10 to-pink-600/5"
  },
};

interface InsightModalProps {
  slug: string | null;
  onClose: () => void;
  articleSlug?: string;
  articleTitle?: string;
}

function InsightModalContent({ slug, onClose, articleSlug, articleTitle }: InsightModalProps) {
  const { data: insight, isLoading } = useQuery<GlossaryEntry>({
    queryKey: ["/api/insights", slug],
    enabled: !!slug,
  });

  const { data: sponsorship } = useQuery<CampaignWithPlacements | null>({
    queryKey: ["/api/insights", slug, "sponsorship"],
    enabled: !!slug,
  });

  const modalLogoPlacement = sponsorship?.placements?.find(p => p.placementType === "modal_logo");
  const modalLogoAsset = modalLogoPlacement?.asset;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-10 w-32" />
      </div>
    );
  }

  if (!insight) {
    return (
      <div className="text-center py-8">
        <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Insight not found</p>
      </div>
    );
  }

  const typeConfig = insight.keywordType ? KEYWORD_TYPE_CONFIG[insight.keywordType] : null;
  const TypeIcon = typeConfig?.icon || Cpu;

  return (
    <>
      <DialogHeader className={`pb-4 -mx-6 -mt-6 px-6 pt-6 rounded-t-lg ${typeConfig?.headerBg || "bg-muted/30"}`}>
        <div className="flex items-center gap-2 mb-2">
          {typeConfig && (
            <Badge className={typeConfig.color}>
              <TypeIcon className="h-3 w-3 mr-1" />
              {typeConfig.label}
            </Badge>
          )}
          {insight.categoryTags?.slice(0, 2).map((tag) => (
            <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
          ))}
        </div>
        <DialogTitle className="text-xl font-bold" data-testid="text-insight-modal-title">
          {insight.title || insight.term}
        </DialogTitle>
        {insight.definition && (
          <DialogDescription className="text-base mt-2" data-testid="text-insight-modal-definition">
            {insight.definition}
          </DialogDescription>
        )}
      </DialogHeader>

      <div className="space-y-4 py-4">
        {insight.insightSummary && (
          <div className="p-4 bg-muted/50 rounded-lg border-l-4 border-primary">
            <p className="text-sm leading-relaxed" data-testid="text-insight-modal-summary">
              {insight.insightSummary}
            </p>
          </div>
        )}

        {insight.whyItMatters && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Lightbulb className="h-4 w-4" />
              Why It Matters
            </div>
            <div 
              className="text-sm prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: truncateHtml(insight.whyItMatters, 200) }}
            />
          </div>
        )}

        {insight.inPractice && !insight.whyItMatters && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Wrench className="h-4 w-4" />
              In Practice
            </div>
            <div 
              className="text-sm prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: truncateHtml(insight.inPractice, 200) }}
            />
          </div>
        )}

        {insight.commonPitfalls && !insight.whyItMatters && !insight.inPractice && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <AlertTriangle className="h-4 w-4" />
              Common Pitfalls
            </div>
            <div 
              className="text-sm prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: truncateHtml(insight.commonPitfalls, 200) }}
            />
          </div>
        )}
      </div>

      {modalLogoAsset && (
        <div className="bg-muted/30 rounded-lg p-4 mt-4 border" data-testid="sponsor-section">
          <div className="flex items-center gap-4">
            {modalLogoAsset.imageUrl && (
              <div className="flex-shrink-0">
                <img 
                  src={getAssetImageUrl(modalLogoAsset)} 
                  alt={modalLogoAsset.name}
                  className="h-12 w-auto object-contain"
                  data-testid="sponsor-logo"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {modalLogoAsset.overlayText && (
                <p className="text-sm text-muted-foreground mb-1" data-testid="sponsor-text">
                  {modalLogoAsset.overlayText}
                </p>
              )}
              {(modalLogoPlacement?.ctaUrl || modalLogoAsset.defaultCtaUrl) && (
                <a 
                  href={modalLogoPlacement?.ctaUrl || modalLogoAsset.defaultCtaUrl || "#"} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                  data-testid="sponsor-cta"
                >
                  {modalLogoPlacement?.ctaLabel || modalLogoAsset.defaultCtaLabel || "Learn More"}
                  <ArrowRight className="ml-1 h-3 w-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t gap-4">
        <Button variant="outline" onClick={onClose} data-testid="button-insight-modal-close">
          Close
        </Button>
        <Button asChild data-testid="button-insight-modal-read-more">
          <Link href={articleSlug ? `/insights/${slug}?from=${encodeURIComponent(articleSlug)}` : `/insights/${slug}`} onClick={onClose}>
            Read Full Insight
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </>
  );
}

function truncateHtml(html: string, maxLength: number): string {
  const text = html.replace(/<[^>]*>/g, "");
  if (text.length <= maxLength) return html;
  
  const truncated = text.substring(0, maxLength).trim();
  const lastSpace = truncated.lastIndexOf(" ");
  const cleanTruncated = lastSpace > maxLength * 0.7 ? truncated.substring(0, lastSpace) : truncated;
  
  return `<p>${cleanTruncated}...</p>`;
}

export function InsightModal({ slug, onClose, articleSlug, articleTitle }: InsightModalProps) {
  return (
    <Dialog open={!!slug} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg" data-testid="insight-modal">
        {slug && <InsightModalContent slug={slug} onClose={onClose} articleSlug={articleSlug} articleTitle={articleTitle} />}
      </DialogContent>
    </Dialog>
  );
}

interface InsightProviderProps {
  children: React.ReactNode;
  articleSlug?: string;
  articleTitle?: string;
}

interface InsightContextValue {
  openInsight: (slug: string) => void;
  glossaryEntries: GlossaryEntry[];
  articleSlug?: string;
  articleTitle?: string;
}

const InsightContext = createContext<InsightContextValue | null>(null);

export function useInsight() {
  const context = useContext(InsightContext);
  if (!context) {
    throw new Error("useInsight must be used within an InsightProvider");
  }
  return context;
}

export function InsightProvider({ children, articleSlug, articleTitle }: InsightProviderProps) {
  const [activeSlug, setActiveSlug] = useState<string | null>(null);

  const { data: entries = [] } = useQuery<GlossaryEntry[]>({
    queryKey: ["/api/glossary"],
  });

  const openInsight = (slug: string) => {
    setActiveSlug(slug);
  };

  return (
    <InsightContext.Provider value={{ openInsight, glossaryEntries: entries, articleSlug, articleTitle }}>
      {children}
      <InsightModal 
        slug={activeSlug} 
        onClose={() => setActiveSlug(null)}
        articleSlug={articleSlug}
        articleTitle={articleTitle}
      />
    </InsightContext.Provider>
  );
}
